/**
 * THE LOSS FILE: WHAT A RESTORATION JOB CARRIES THAT A REPAIR DOES NOT.
 *
 * Anthony, 2026-10-28, for Peter Banyai's RemediationX and Nevada Water
 * Damage: "build for water damage and what they do". The brief named the
 * fields: loss type, category and class of water, insurance carrier, claim
 * number, adjuster, moisture readings, equipment on site, drying days and
 * photos.
 *
 * Photos already have a home on every job, and the loss type, category,
 * class, area and claim number sit in the identity boxes the vocabulary
 * relabels (src/domain/trade.ts, 'restoration'). What is left lives here,
 * on one jsonb column, jobs.loss (migration 0235), so nothing about the
 * other trades' rows moves.
 *
 * Pure, so the card and the tests read the record the same way. A record
 * written by an older card, or by hand, or not at all, comes back as a
 * complete shape with nothing in it, never as an error on a tech's phone.
 */

/** IICRC S500 water categories, said the way an adjuster says them. */
export const CATEGORIES = [
  { key: "1", label: "Cat 1, clean water" },
  { key: "2", label: "Cat 2, gray water" },
  { key: "3", label: "Cat 3, black water" },
] as const;

/** IICRC S500 classes, by how much got wet and how deep. */
export const CLASSES = [
  { key: "1", label: "Class 1, small area" },
  { key: "2", label: "Class 2, a whole room" },
  { key: "3", label: "Class 3, ceilings and walls" },
  { key: "4", label: "Class 4, wood, plaster, concrete" },
] as const;

/** The kinds of loss this trade takes, for the loss type box. */
export const LOSS_TYPES = ["Water", "Mold", "Fire and smoke", "Sewage", "Storm"] as const;

/** The machines a crew sets and has to bill by the day. */
export const EQUIPMENT = [
  { key: "air_movers", label: "Air movers" },
  { key: "dehus", label: "Dehumidifiers" },
  { key: "scrubbers", label: "Air scrubbers" },
  { key: "negative_air", label: "Negative air machines" },
] as const;
export type EquipmentKey = typeof EQUIPMENT[number]["key"];

export type Insurance = {
  carrier?: string;
  adjuster?: string;
  adjuster_phone?: string;
  adjuster_email?: string;
  deductible?: number | null;
  policy?: string;
};

export type Reading = {
  /** ISO date, the day it was taken. */
  at: string;
  /** Room and material, "Kitchen, drywall". */
  where: string;
  /** Moisture content as the meter shows it. */
  value: number;
  /** The dry goal for that material, if somebody set one. */
  goal?: number | null;
};

export type LossRecord = {
  /** ISO date of the loss itself, which the carrier asks for first. */
  loss_date?: string;
  category?: string;
  cls?: string;
  insurance: Insurance;
  equipment: Partial<Record<EquipmentKey, number>>;
  /** ISO dates. Drying ends when the last reading hits its goal. */
  drying_start?: string;
  drying_end?: string;
  readings: Reading[];
};

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim());
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const isoDay = (v: unknown): string => {
  const s = str(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
};

/** Whatever is in jobs.loss, as a complete record. Never throws. */
export function readLoss(raw: unknown): LossRecord {
  const o = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
  const ins = o["insurance"] && typeof o["insurance"] === "object" ? o["insurance"] as Record<string, unknown> : {};
  const eq = o["equipment"] && typeof o["equipment"] === "object" ? o["equipment"] as Record<string, unknown> : {};
  const equipment: Partial<Record<EquipmentKey, number>> = {};
  for (const e of EQUIPMENT) {
    const n = num(eq[e.key]);
    if (n !== null && n > 0) equipment[e.key] = Math.round(n);
  }
  const readings = Array.isArray(o["readings"]) ? (o["readings"] as unknown[]) : [];
  return {
    loss_date: isoDay(o["loss_date"]) || undefined,
    category: CATEGORIES.some((c) => c.key === str(o["category"])) ? str(o["category"]) : undefined,
    cls: CLASSES.some((c) => c.key === str(o["cls"])) ? str(o["cls"]) : undefined,
    insurance: {
      carrier: str(ins["carrier"]),
      adjuster: str(ins["adjuster"]),
      adjuster_phone: str(ins["adjuster_phone"]),
      adjuster_email: str(ins["adjuster_email"]),
      deductible: num(ins["deductible"]),
      policy: str(ins["policy"]),
    },
    equipment,
    drying_start: isoDay(o["drying_start"]) || undefined,
    drying_end: isoDay(o["drying_end"]) || undefined,
    readings: readings
      .map((r) => r && typeof r === "object" ? r as Record<string, unknown> : {})
      .map((r) => ({ at: isoDay(r["at"]), where: str(r["where"]), value: num(r["value"]), goal: num(r["goal"]) }))
      .filter((r) => Boolean(r.at) && r.value !== null)
      .map((r): Reading => ({ at: r.at, where: r.where, value: r.value as number, goal: r.goal }))
      .sort((a, b) => a.at.localeCompare(b.at)),
  };
}

/**
 * HOW MANY DAYS IT HAS BEEN DRYING. Counted the way equipment is billed,
 * by the calendar day, so the day it was set counts and so does the day it
 * was pulled. Still running counts up to `today`. Nothing set yet is null.
 */
export function dryingDays(rec: LossRecord, today: string): number | null {
  if (!rec.drying_start) return null;
  const end = rec.drying_end ?? today.slice(0, 10);
  const a = Date.parse(`${rec.drying_start}T00:00:00Z`);
  const b = Date.parse(`${end}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return Math.round((b - a) / 86_400_000) + 1;
}

/** Machines on site right now, as one number for the board. */
export function machinesOnSite(rec: LossRecord): number {
  if (rec.drying_end) return 0;
  return Object.values(rec.equipment).reduce((n, v) => n + (v ?? 0), 0);
}

/**
 * WHETHER A SPOT IS DRY. The newest reading for each place, against its
 * goal. A place with no goal is not judged, because guessing a material's
 * dry standard is how a wall gets closed up wet.
 */
export function wetSpots(rec: LossRecord): Reading[] {
  const latest = new Map<string, Reading>();
  for (const r of rec.readings) latest.set(r.where.toLowerCase(), r);
  return [...latest.values()].filter((r) => r.goal != null && r.value > r.goal);
}

/** One line for an alert or the top of the card. Empty when nothing is set. */
export function lossLine(rec: LossRecord, today: string): string {
  const bits: string[] = [];
  const cat = CATEGORIES.find((c) => c.key === rec.category);
  const cls = CLASSES.find((c) => c.key === rec.cls);
  if (cat) bits.push(`Cat ${cat.key}`);
  if (cls) bits.push(`Class ${cls.key}`);
  if (rec.insurance.carrier) bits.push(rec.insurance.carrier);
  const days = dryingDays(rec, today);
  if (days !== null) bits.push(`${rec.drying_end ? "dried in" : "drying day"} ${days}${rec.drying_end ? " days" : ""}`);
  const on = machinesOnSite(rec);
  if (on) bits.push(`${on} machine${on === 1 ? "" : "s"} on site`);
  return bits.join(", ");
}
